import React from "react";

export const trendLockContent = {
  create_lock: {
    title: "Create a Lock",
    content: (
      <>
        <h3>Create a Lock</h3>
        <p>
          This guide explains how to use TrendLock, TrendPad's audited tool for
          locking tokens or Liquidity Pool (LP) tokens. You can create simple
          time locks or set up vesting schedules for gradual release. This is
          commonly used for team tokens, marketing allocations, or locking
          liquidity to build investor trust.
        </p>
        <p>After reading this, you will understand:</p>
        <ul>
          <li>The purpose of TrendLock and its audit status.</li>
          <li>How to create a simple time lock (Method 1).</li>
          <li>How to create a lock with vesting (Method 2).</li>
          <li>
            The specific parameters required for each method, using original
            descriptions where preferred.
          </li>
          <li>
            The crucial step of excluding the TrendLock address from fees for
            taxed tokens.
          </li>
          <li>
            How locking information can be used to display tokenomics on your
            presale page.
          </li>
        </ul>
        <p>
          <strong>Note:</strong> You can use TrendLock to generate lock
          information that helps populate the tokenomic chart displayed on your
          TrendPad presale/fair launch page.
        </p>
        <p>
          <strong>Note:</strong> TrendLock has been audited by CertiK and
          potentially other firms. View the CertiK audit here:{" "}
          <a
            href="https://skynet.certik.com/projects/trendpad"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://skynet.certik.com/projects/trendpad
          </a>
        </p>

        <h4>TrendLock Interface Overview</h4>

        <h4>Method 1: Create a Lock Without Vesting (Simple Time Lock)</h4>
        <p>
          Use this method to lock a specific amount of tokens until a set date
          and time.
        </p>

        <h5>Step 1: Access TrendLock</h5>
        <ul>
          <li>
            Ensure your wallet is connected to TrendPad on the correct network.
          </li>
          <li>
            From the TrendPad homepage, click "TrendLock" =&gt; "Create Lock",
            or go directly to the Create Lock page.
          </li>
        </ul>

        <h5>Step 2: Configure Lock Settings</h5>
        <ul>
          <li>
            Input the contract address of the token or LP token you want to
            lock.
          </li>
          <li>
            By default, the connected wallet is the owner. Check "use another
            owner" and input a different wallet address if needed.
          </li>
          <li>
            Give this lock a descriptive title (e.g., "Team Tokens Lock",
            "Marketing Wallet Lock").
          </li>
          <li>
            Enter the total number of tokens to lock (Enter a number, not a
            percentage).
          </li>
          <li>
            Select the exact date and time (in UTC) when the tokens will become
            unlockable.
          </li>
        </ul>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/1.png"
            alt="Enter Token Address and Lock Details"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <h5>Step 3: Approve Tokens</h5>
        <p>
          Click the "Approve" button and confirm the transaction in your wallet
          to allow TrendLock to handle the specified token amount.
        </p>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/2.png"
            alt="Approve Transaction and Pay Fees"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <h5>Step 4: Create Lock</h5>
        <p>
          After approval, click the "Lock" button and confirm the final
          transaction in your wallet to create the time lock.
        </p>
        <p>
          When the lock is successful, you will be redirected to the success
          page.
        </p>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/3.png"
            alt="Lock Creation Successful"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <p>
          <strong>Important Note (Both Methods):</strong> If the token you are
          locking has transfer fees, rewards, or max transaction limits, you
          must exclude the TrendLock lockup address{" "}
          <code>0x5E5b9bE5fd939c578ABE5800a90C566eeEbA44a5</code> from these
          features in the token's contract before clicking "Lock". Failure to do
          so will likely cause the lock transaction to fail.
        </p>

        <h4>Method 2: Create a Lock Using Vesting</h4>
        <p>
          Use this method to lock tokens and release them gradually over time
          based on a schedule.
        </p>

        <h5>Step 1: Access TrendLock</h5>
        <ul>
          <li>
            Ensure your wallet is connected to TrendPad on the correct network.
          </li>
          <li>
            From the TrendPad homepage, click "TrendLock" =&gt; "Create Lock".
          </li>
        </ul>

        <h5>Step 2: Configure Lock Settings</h5>
        <ul>
          <li>Input the contract address of the token you want to vest.</li>
          <li>
            By default, the connected wallet is the owner. Check "use another
            owner" and input a different wallet address if needed.
          </li>
          <li>
            Give this lock a descriptive title (e.g., "Advisor Vesting
            Schedule").
          </li>
          <li>
            Enter the total number of tokens to be included in this vesting
            schedule.
          </li>
          <li>Place a check in the check box "use vesting".</li>
        </ul>

        <h5>Step 3: Configure Vesting Parameters</h5>
        <ul>
          <li>
            <strong>TGE Date:</strong> Should be the listing time.
          </li>
          <li>
            <strong>TGE (percent):</strong> The percentage of the first batch of
            team tokens to be released.
          </li>
          <li>
            <strong>Cycle (days):</strong> How long, in days, between each batch
            of vested tokens is released.
          </li>
          <li>
            <strong>Cycle Release (percent):</strong> How many tokens will be
            released each cycle following the First Token Release batch.
          </li>
        </ul>

        <h5>Step 4: Approve Tokens</h5>
        <p>
          Click "Approve" and confirm the transaction in your wallet to allow
          TrendLock to handle the tokens.
        </p>

        <h5>Step 5: Create Lock</h5>
        <p>
          Click "Lock" and confirm the final transaction in your wallet to
          create the vesting lock.
        </p>
        <p>
          <strong>Important Notes (Vesting):</strong> Exclude the TrendLock
          address <code>0x5E...A44a5</code> from fees/rewards/limits in your
          token contract before locking if applicable. Team Vesting doesn't
          support rebase tokens.
        </p>

        <h4>Q&A (FAQs)</h4>
        <p>
          <strong>
            Q1: What's the difference between locking Tokens and LP Tokens?
          </strong>
        </p>
        <p>
          A: Locking Tokens refers to locking your project's own native token
          (like team or marketing allocation). Locking LP Tokens refers to
          locking the Liquidity Pool tokens you receive after adding liquidity
          on a DEX; this proves liquidity is locked and cannot be removed
          ("rugged") for the specified duration. Both use the same TrendLock
          interface.
        </p>

        <p>
          <strong>
            Q2: Why is excluding the TrendLock address from fees important?
          </strong>
        </p>
        <p>
          A: When you click "Lock", the tokens are transferred to the TrendLock
          contract (0x5E...A44a5). If your token taxes this transfer, the lock
          contract receives fewer tokens than intended, causing issues with
          unlocking the correct amount later. Excluding the address ensures the
          full amount is locked without tax.
        </p>

        <p>
          <strong>
            Q3: Can I unlock tokens before the set time or vesting date?
          </strong>
        </p>
        <p>
          A: No. The core purpose of TrendLock is to enforce the lock duration
          or vesting schedule immutably on the blockchain. Tokens can only be
          withdrawn by the designated owner after the lock expires or the
          vesting period arrives.
        </p>

        <p>
          <strong>Q4: What happens after the simple lock duration ends?</strong>
        </p>
        <p>
          A: The designated owner can go to the TrendLock page, find the
          specific lock entry, and click an "Unlock" or "Withdraw" button to
          claim the full amount of locked tokens back to their wallet.
        </p>

        <p>
          <strong>Q5: How does claiming work for vested tokens?</strong>
        </p>
        <p>
          A: Similar to the simple lock, the designated owner visits the
          TrendLock page. When the TGE date or a cycle end date passes, an
          "Unlock" button appears for that specific vesting lock, allowing the
          owner to claim the portion of tokens that became available during that
          period. Unclaimed amounts accumulate.
        </p>

        <h4>Troubleshooting</h4>
        <p>
          <strong>Q: My "Approve" or "Lock" transaction fails.</strong>
        </p>
        <p>
          A: Ensure you have enough native currency (BNB, ETH, etc.) for gas
          fees. Crucially, check if the token being locked has
          fees/rewards/limits and if you have correctly excluded the TrendLock
          address (0x5E...A44a5) in the token contract BEFORE attempting to
          lock. Confirm you are using the wallet that holds the tokens you
          intend to lock.
        </p>

        <p>
          <strong>
            Q: I made a mistake in the lock duration or vesting schedule after
            clicking "Lock". Can I change it?
          </strong>
        </p>
        <p>
          A: No. Once a lock is confirmed on the blockchain via the "Lock"
          transaction, the parameters (duration, vesting schedule, amount,
          owner) are generally permanent and cannot be modified. Plan carefully
          beforehand.
        </p>

        <p>
          <strong>
            Q: I can't find my lock on the TrendLock page after creating it.
          </strong>
        </p>
        <p>
          A: First, ensure you are connected with the correct wallet (usually
          the one that created the lock or the designated owner wallet) and are
          on the correct blockchain network. Check the transaction hash on the
          block explorer to confirm the lock transaction was successful. Then,
          navigate within TrendPad: go to the TrendLock section, select the
          correct tab for what you locked (e.g., "Token" or "Liquidity"), and
          then click on the "My Locks" tab. Your successfully created locks
          associated with the connected wallet should appear there. Try
          refreshing the page if needed.
        </p>
      </>
    ),
  },
  update_lock: {
    title: "How to Update the Lock",
    content: (
      <>
        <h3>How to Update the Lock</h3>
        <p>
          If you need to extend the lock duration or increase the locked amount,
          you can use the update feature.
        </p>

        <h4>Step 1: Access the Update Feature</h4>
        <ul>
          <li>
            Navigate to your lock record (as described in "How to Unlock").
          </li>
          <li>Click on the specific lock to view its details.</li>
          <li>
            Find and click the <strong>Update Lock</strong> button.
          </li>
        </ul>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/4.png"
            alt="Click Update Lock Button"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <h4>Step 2: Modify Lock Parameters</h4>
        <p>A modal will appear where you can update the following:</p>
        <ul>
          <li>
            <strong>Add More Tokens:</strong> Input the additional amount of
            tokens you want to lock.
          </li>
          <li>
            <strong>Extend Lock Time:</strong> Select a new date and time for
            when the tokens will be unlockable. Note: You can only{" "}
            <em>extend</em> the time; you cannot shorten it.
          </li>
        </ul>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/5.png"
            alt="Update Lock Modal"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <h4>Step 3: Confirm Update</h4>
        <p>
          Click the <strong>Update Lock</strong> button in the modal and confirm
          the transaction in your wallet to apply the changes.
        </p>
      </>
    ),
  },
  how_to_unlock: {
    title: "How to Unlock",
    content: (
      <>
        <h3>How to Unlock</h3>
        <p>
          This guide explains how the designated owner can unlock tokens
          previously locked using TrendPad's TrendLock feature. This covers both
          simple time locks (unlocking the full amount after expiry) and vesting
          locks (claiming tokens as each cycle completes).
        </p>
        <p>Reading this will help you:</p>
        <ul>
          <li>
            Understand the process for unlocking tokens from a simple time lock.
          </li>
          <li>
            Understand how to claim tokens released through a vesting schedule.
          </li>
          <li>
            Know where to find your locks and initiate the unlock/claim process.
          </li>
          <li>
            Be aware of the serious implications of using the "Renounce Lock
            Ownership" function.
          </li>
          <li>
            Find answers to common questions about unlocking and claiming.
          </li>
        </ul>

        <h4>Method 1: Unlock Tokens Without Vesting (Simple Time Lock)</h4>
        <p>
          Once the lock duration you set has expired, the designated owner can
          withdraw the locked tokens. <strong>Important:</strong> Only the owner
          of the lock will be able to unlock the tokens, and only after the
          completion of the unlock date.
        </p>

        <h5>Step 1: Connect Wallet</h5>
        <p>
          Ensure the wallet designated as the owner of the lock is connected to
          TrendPad on the correct network.
        </p>

        <h5>Step 2: Navigate to Your Lock</h5>
        <p>
          From the TrendPad homepage, navigate to "TrendLock" and select either
          "Token" or "Liquidity" depending on what type of lock you created.
        </p>

        <h5>Step 3: Find and View Your Lock</h5>
        <p>
          Click on the "My Lock" tab to view all locks associated with your
          connected wallet. Locate the specific lock entry you wish to unlock
          and click on "View" to be redirected to the detailed view page for
          that lock.
        </p>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/11.png"
            alt="Click My Lock and View to access unlock page"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>

        <h5>Step 4: Unlock Your Tokens</h5>
        <p>
          On the lock details page, you will see comprehensive information about
          your lock including the unlock date and token amount. Once the unlock
          date has passed and you are connected with the owner wallet, the
          "Unlock" button will become active.
        </p>
        <p>
          Click the <strong>Unlock</strong> button to initiate the withdrawal
          process.
        </p>

        <h5>Step 5: Confirm Transaction in MetaMask</h5>
        <p>
          After clicking "Unlock", MetaMask (or your connected wallet) will
          appear with a transaction confirmation popup. The popup will display
          the network gas fee required for the withdrawal transaction. Review
          the details carefully and click "Confirm" to complete the unlock
          process.
        </p>

        <h5>Step 6: Unlock Confirmation</h5>
        <p>
          After the transaction is confirmed on the blockchain, you will see a
          success message indicating that your lock has been unlocked. The
          tokens will be transferred to the designated owner wallet.
        </p>
        <div style={{ margin: "20px 0" }}>
          <img
            src="/docs/lock/12.png"
            alt="Lock successfully unlocked confirmation"
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </div>
        <p>
          <strong>Note:</strong> The unlock process is irreversible. Once you
          confirm the transaction, the tokens will be withdrawn from the lock
          contract and transferred to the owner's wallet.
        </p>

        <h4>Method 2: Unlock/Claim Vested Tokens</h4>
        <p>
          When each vesting cycle ends (either the TGE date or the end of a
          subsequent cycle), the designated owner can claim the portion of
          tokens that became available during that period.
        </p>
        <ol>
          <li>
            <strong>Connect & Navigate:</strong> Follow steps 1-3 from Method 1
            above (Connect owner wallet, go to TrendLock -&gt; Token -&gt; My
            Lock -&gt; View the specific vesting lock).
          </li>
          <li>
            <strong>Claim Vested Tokens:</strong> If tokens are available based
            on the vesting schedule (i.e., TGE or a cycle has completed), the
            "Unlock" button will be active. This button effectively functions as
            "Claim" for vesting locks. Click "Unlock".
          </li>
          <li>
            <strong>Confirm Transaction:</strong> Confirm the transaction in
            your wallet (e.g., MetaMask) and pay the gas fee to receive the
            currently claimable batch of vested tokens.
          </li>
        </ol>
        <p>
          <strong>Accumulated Claims:</strong> If the owner doesn't claim tokens
          exactly when a cycle expires, they can do so at any later time. If
          multiple cycles have passed, clicking "Unlock" will withdraw the total
          accumulated amount available from all completed past cycles up to that
          point.
        </p>

        <h4>Renounce Lock Ownership (Use Extreme Caution!)</h4>
        <p>
          There is an option on the lock details page to "Renounce Lock
          Ownership".
        </p>
        <ul>
          <li>
            <strong>Action:</strong> Clicking this and confirming the
            transaction permanently transfers the ownership of that specific
            lock to a dead address (an address nobody controls, like
            0x00...00dEaD).
          </li>
          <li>
            <strong>Consequence:</strong> This means NO ONE (including the
            original owner) will EVER be able to unlock or claim ANY remaining
            tokens from that lock, even after the lock period or vesting
            schedule completes. The tokens become permanently inaccessible.
          </li>
          <li>
            <strong>Warning:</strong> Only use this function if you fully
            understand its irreversible nature and intend for the locked tokens
            to be permanently removed from circulation after the lock is
            renounced. TrendPad accepts no liability for any loss resulting from
            this action.
          </li>
        </ul>

        <h4>Q&A (FAQs)</h4>
        <p>
          <strong>Q1: Who can unlock the tokens?</strong>
        </p>
        <p>
          A: Only the wallet address designated as the "owner" when the lock was
          created can initiate the unlock/claim transaction.
        </p>

        <p>
          <strong>
            Q2: Do I have to unlock tokens exactly when the lock expires or a
            vesting cycle ends?
          </strong>
        </p>
        <p>
          A: No. For simple locks, you can unlock anytime after the expiry date.
          For vesting, you can claim available tokens anytime after the relevant
          cycle completes. Unclaimed amounts from previous cycles accumulate and
          can be claimed together later.
        </p>

        <p>
          <strong>Q3: Do I need to pay gas fees to unlock or claim?</strong>
        </p>
        <p>
          A: Yes, every time you click "Unlock" and confirm in your wallet, it's
          a blockchain transaction that requires payment of network gas fees (in
          the chain's native currency like BNB, ETH, etc.).
        </p>

        <p>
          <strong>
            Q4: What is the difference between unlocking a Token lock and an LP
            Token lock?
          </strong>
        </p>
        <p>
          A: The process using TrendLock is identical. You navigate to either
          the "Token" or "Liquidity" section under TrendLock, find your lock in
          "My Locks", and click "Unlock" after expiry. The difference is simply
          what you are unlocking – either your project's native token or the LP
          tokens representing your share of the DEX liquidity pool.
        </p>

        <h4>Troubleshooting</h4>
        <p>
          <strong>Q: The "Unlock" button is greyed out or not visible.</strong>
        </p>
        <p>
          A: Ensure you are connected with the correct owner wallet address that
          was set when the lock was created. Double-check that the lock expiry
          date (for simple locks) or the TGE/cycle end date (for vesting) has
          actually passed (remember times are usually in UTC). Refresh the page.
          Make sure you are viewing the correct lock entry under "My Locks".
        </p>

        <p>
          <strong>
            Q: I clicked "Unlock" and confirmed, but the transaction failed.
          </strong>
        </p>
        <p>
          A: Ensure you have enough native currency (BNB, ETH, etc.) in the
          owner wallet to pay for the gas fees. Network congestion might cause
          temporary failures. Check the specific error message on the block
          explorer; sometimes interacting with lock contracts requires a
          slightly higher gas limit.
        </p>

        <p>
          <strong>
            Q: I accidentally renounced ownership of a lock. Can I get the
            tokens back?
          </strong>
        </p>
        <p>
          A: No. Renouncing ownership is irreversible. The tokens associated
          with that specific lock are permanently lost and cannot be recovered
          by anyone once ownership is sent to the dead address.
        </p>
      </>
    ),
  },
  transfer_lock_ownership: {
    title: "Transfer Lock Ownership",
    content: (
      <>
        <h3>Transfer Lock Ownership</h3>
        <p>
          This guide explains how to transfer the ownership of an existing
          TrendLock lock (either a simple time lock or a vesting lock) to a
          different wallet address. The owner address is the only one that can
          unlock or claim tokens from the lock.
        </p>
        <p>Reading this will help you:</p>
        <ul>
          <li>
            Understand when and why you might need to transfer lock ownership.
          </li>
          <li>
            Follow the simple steps to initiate an ownership transfer via the
            TrendLock interface.
          </li>
          <li>
            Know where to find the transfer function for your specific lock.
          </li>
          <li>
            Complete the process by confirming the transaction in your wallet.
          </li>
        </ul>

        <h4>Steps to Transfer Lock Ownership</h4>
        <p>
          If you need to change the designated wallet address that controls a
          specific token or LP token lock you previously created, follow these
          steps:
        </p>
        <ol>
          <li>
            <strong>Connect Owner Wallet:</strong> Ensure the wallet that
            currently owns the lock is connected to TrendPad on the correct
            network.
          </li>
          <li>
            <strong>Navigate to Your Lock:</strong>
            <ul>
              <li>From the TrendPad homepage, go to "TrendLock".</li>
              <li>
                Select the correct category ("Token" or "Liquidity") for the
                lock you want to transfer.
              </li>
              <li>Click on the "My Lock" tab.</li>
              <div style={{ margin: "20px 0" }}>
                <img
                  src="/docs/lock/6.png"
                  alt="Go to My Locks"
                  style={{ maxWidth: "100%", borderRadius: "8px" }}
                />
              </div>
              <li>
                Locate the specific lock entry you wish to transfer ownership of
                and click on "View".
              </li>
              <div style={{ margin: "20px 0" }}>
                <img
                  src="/docs/lock/7.png"
                  alt="View Lock Details"
                  style={{ maxWidth: "100%", borderRadius: "8px" }}
                />
              </div>
            </ul>
          </li>
          <li>
            <strong>Initiate Transfer:</strong> On the lock details page, find
            and click the "Transfer Lock Ownership" button.
          </li>
          <div style={{ margin: "20px 0" }}>
            <img
              src="/docs/lock/8.png"
              alt="Transfer Lock Ownership Button"
              style={{ maxWidth: "100%", borderRadius: "8px" }}
            />
          </div>
          <li>
            <strong>Enter New Owner & Confirm:</strong>
            <ul>
              <li>
                A pop-up or field will appear. Enter the wallet address of the
                new owner you want to transfer control to. Double-check the
                address for accuracy.
              </li>
              <li>
                Click the “Transfer Ownership” button (or similar confirmation
                button).
              </li>
              <li>
                Your connected wallet (e.g., MetaMask) will ask you to confirm
                the transaction. It will show the network gas fee required.
                Review the details and click “Confirm”.
              </li>
            </ul>
          </li>
          <div style={{ margin: "20px 0" }}>
            <img
              src="/docs/lock/10.png"
              alt="Transfer Ownership Modal"
              style={{ maxWidth: "100%", borderRadius: "8px" }}
            />
          </div>
        </ol>

        <h4>Q&A (FAQs)</h4>
        <p>
          <strong>Q1: Why would I need to transfer lock ownership?</strong>
        </p>
        <p>
          A: You might need to transfer ownership if the original owner wallet
          becomes compromised, if control needs to be passed to a different team
          member or a multi-sig wallet, or if the designated recipient changes.
        </p>

        <p>
          <strong>Q2: Who can initiate the transfer?</strong>
        </p>
        <p>
          A: Only the wallet address that currently owns the specific TrendLock
          entry can initiate the transfer to a new owner.
        </p>

        <p>
          <strong>Q3: Is transferring ownership reversible?</strong>
        </p>
        <p>
          A: No. Once you confirm the transaction and ownership is transferred
          to the new address, only that new address will have the ability to
          unlock/claim tokens or transfer ownership again. The original owner
          loses all control over that specific lock.
        </p>

        <p>
          <strong>Q4: Does this transfer the locked tokens themselves?</strong>
        </p>
        <p>
          A: No, this only transfers the control or ownership rights over the
          lock contract entry. The tokens remain locked according to the
          original schedule. The new owner simply gains the ability to
          unlock/claim them when they become available.
        </p>

        <h4>Troubleshooting</h4>
        <p>
          <strong>
            Q: The "Transfer Lock Ownership" button is missing or greyed out.
          </strong>
        </p>
        <p>
          A: Ensure you are connected with the wallet that is the current owner
          of the lock. If you are connected with a different wallet, you won't
          have permission to transfer. Double-check you are viewing the correct
          lock entry.
        </p>

        <p>
          <strong>Q: My transaction to transfer ownership failed.</strong>
        </p>
        <p>
          A: Make sure you have enough native currency (BNB, ETH, etc.) in the
          current owner wallet to pay the gas fee. Ensure the new owner address
          you entered is a valid wallet address format for the relevant
          blockchain. Network congestion could also cause temporary issues.
          Check the block explorer for specific error messages.
        </p>

        <p>
          <strong>
            Q: I transferred ownership to the wrong address. Can I get it back?
          </strong>
        </p>
        <p>
          A: No. Ownership transfers on the blockchain are generally
          irreversible. Unless the owner of the incorrect address agrees to
          transfer it back to you (which requires them to initiate the transfer
          and pay gas), control is lost. Always double-check the recipient
          address before confirming.
        </p>
      </>
    ),
  },
};
